import { useEffect, useRef, useState } from 'react';

/**
 * @param {number} totalSeconds
 * @returns {string} m:ss
 */
function formatCountdownLabel(totalSeconds) {
  const s = Math.max(0, totalSeconds);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

/**
 * @param {string|undefined} codeExpiresAtIso
 * @param {{ enabled: boolean, onExpired?: () => void }} options
 * @returns {{ remainingLabel: string | null, secondsLeft: number }}
 */
export function useTwoFactorExpiryCountdown(codeExpiresAtIso, { enabled, onExpired }) {
  const [secondsLeft, setSecondsLeft] = useState(0);
  const onExpiredRef = useRef(onExpired);
  const hasFiredRef = useRef(false);

  onExpiredRef.current = onExpired;

  useEffect(() => {
    hasFiredRef.current = false;
  }, [codeExpiresAtIso, enabled]);

  useEffect(() => {
    if (!enabled || !codeExpiresAtIso) {
      setSecondsLeft(0);
      return undefined;
    }
    const exp = new Date(codeExpiresAtIso).getTime();
    if (Number.isNaN(exp)) {
      setSecondsLeft(0);
      return undefined;
    }

    const tick = () => {
      const sec = Math.max(0, Math.floor((exp - Date.now()) / 1000));
      setSecondsLeft(sec);
      if (sec === 0 && onExpiredRef.current && !hasFiredRef.current) {
        hasFiredRef.current = true;
        onExpiredRef.current();
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [codeExpiresAtIso, enabled]);

  if (!codeExpiresAtIso || !enabled) {
    return { remainingLabel: null, secondsLeft: 0 };
  }
  const exp = new Date(codeExpiresAtIso).getTime();
  if (Number.isNaN(exp)) {
    return { remainingLabel: null, secondsLeft: 0 };
  }
  return {
    remainingLabel: formatCountdownLabel(secondsLeft),
    secondsLeft,
  };
}
